/**
 * useDeleteDatabaseConnection Hook
 *
 * Custom React Query mutation hook for deleting database connections.
 * IMPORTANT: Database connection can only be deleted if it has no access grants.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { databaseConnectionService } from '../../../api/database-connection.service';
import { getErrorMessage } from '../../../lib/utils/errors';

interface UseDeleteDatabaseConnectionOptions {
  onSuccess?: () => void;
  onError?: (error: string) => void;
}

/**
 * Hook for deleting database connection
 * @param tenantId - Tenant ID
 * @param options - Success/error callbacks
 */
export const useDeleteDatabaseConnection = (tenantId: string, options?: UseDeleteDatabaseConnectionOptions) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (dbConnectionId: string) => databaseConnectionService.deleteDatabaseConnection(tenantId, dbConnectionId),
    onSuccess: () => {
      // Invalidate database connection list query
      queryClient.invalidateQueries({ queryKey: ['database-connections', tenantId] });
      options?.onSuccess?.();
    },
    onError: (error: unknown) => {
      const errorMessage = getErrorMessage(error);
      options?.onError?.(errorMessage);
    },
  });
};
